import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { getMinQuantity } from 'components/Checkout/CartForm/utils'
import WaitingListModale from 'components/WaitingList/WaitingListModale'
import { Button, ButtonVariant } from 'components/common/Button'
import { Status } from 'components/common/Status/Status'
import RacePageBadge from 'components/event/Badge/RacePageBadge'
import { FrontRaceService } from 'domain/services/RaceService/FrontRaceService'
import { usePriceFormat } from 'hooks/usePriceFormat'
import { LiveEventRace } from 'services/api/database/EventService'
import { EventType } from './EventType'
import {
  AlertBadgeContainer,
  ContentRaceFooter,
  CtaContainer,
  PriceValue,
  RaceCardContainer,
  RaceCardFooter
} from './RaceCard.style'
import { RaceData } from './RaceData'
import { WaitingListFooter } from './WaitingListFooter'

interface FullRaceCardContentProps {
  race: LiveEventRace
  raceStatus: Status
  isValidPrice: boolean
  isWaitingList: boolean
  hasReduction: boolean
  finalPrice: { amount: number; currency: string }
  finalReductionPrice: { amount: number; currency: string }
  ctaLink: string
  handleClick: () => void
  ctaLabel: string
  isFeatured: boolean | null
  showRaceStock: boolean
}

export const FullRaceCardContent = ({
  race,
  raceStatus,
  isValidPrice,
  isWaitingList,
  hasReduction,
  finalPrice,
  finalReductionPrice,
  ctaLink,
  handleClick,
  ctaLabel,
  isFeatured,
  showRaceStock
}: FullRaceCardContentProps) => {
  const { t } = useTranslation(['common', 'event'])
  const [openWaitingListModale, setOpenWaitingListModale] = useState(false)

  const {
    name,
    categoryLevel1: type,
    categoryLevel2: distance,
    price,
    raceData,
    startDate,
    timeZone,
    raceInventory
  } = race

  const { formatPriceDefault } = usePriceFormat()

  const remainingStock = raceInventory
    ? raceInventory.stockedQuantity - raceInventory.reserved
    : null

  const displayAlertBadge =
    [Status.OUT_OF_STOCK, Status.WAITING_LIST].includes(raceStatus) ||
    showRaceStock

  return (
    <>
      <RaceCardContainer
        $noMarginBottom={isWaitingList}
        $noMarginTop={!displayAlertBadge}
      >
        <Stack gap={1.3}>
          <Typography variant="h6" lineHeight={1.2}>
            {name}
          </Typography>
          <RaceData
            raceData={raceData}
            timeZone={timeZone}
            startDate={startDate}
          />
          {type && <EventType type={type} distance={distance} />}
        </Stack>
        <RaceCardFooter $isValidPrice={isValidPrice} $largerMargin={!startDate}>
          <ContentRaceFooter>
            {isValidPrice ? (
              <Stack
                justifyContent="center"
                alignItems={{ xs: 'flex-start', md: 'flex-end' }}
              >
                {race.showPrice ? (
                  <>
                    <PriceValue $hasReduction={hasReduction} variant="body2">
                      {price === 0
                        ? t('free')
                        : formatPriceDefault(
                            finalPrice.amount,
                            finalPrice.currency,
                            getMinQuantity(race)
                          )}
                    </PriceValue>
                    {hasReduction && (
                      <PriceValue variant="body2" $isRed>
                        {finalReductionPrice.amount === 0
                          ? t('free')
                          : formatPriceDefault(
                              finalReductionPrice.amount,
                              finalReductionPrice.currency,
                              getMinQuantity(race)
                            )}
                      </PriceValue>
                    )}
                    {FrontRaceService.displayPerParticipantLabel(race) && (
                      <Typography
                        variant="bodyMRegular"
                        color="label.subdued"
                        whiteSpace="nowrap"
                      >
                        {t('event:perParticipant')}
                      </Typography>
                    )}
                  </>
                ) : null}
              </Stack>
            ) : null}
            {ctaLink && (
              <CtaContainer>
                {displayAlertBadge && (
                  <AlertBadgeContainer>
                    <RacePageBadge
                      remainingStock={showRaceStock ? remainingStock : 0}
                    />
                  </AlertBadgeContainer>
                )}
                <Button
                  variant={
                    !isFeatured
                      ? ButtonVariant.Default
                      : raceStatus !== Status.ONLINE
                      ? ButtonVariant.Disabled
                      : ButtonVariant.Primary
                  }
                  label={ctaLabel}
                  onClick={handleClick}
                />
              </CtaContainer>
            )}
          </ContentRaceFooter>
        </RaceCardFooter>
      </RaceCardContainer>
      {isWaitingList && (
        <WaitingListFooter
          openWaitingListModale={() => setOpenWaitingListModale(true)}
        />
      )}
      <WaitingListModale
        open={openWaitingListModale}
        onClose={() => setOpenWaitingListModale(false)}
        race={race}
      />
    </>
  )
}
